import { CellSort, EmptyRowsFallback } from '@shared/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { DataTable } from '@shared/ui/DataTable';
import { Text, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconChecks } from '@tabler/icons-react';
import {
  CandidateStatusBadge,
  getCandidateDisplayName,
  type Candidate,
} from '@entities/candidates';
import dayjs from 'dayjs';
import { useFeatureFlag } from '@packages/feature-flags/react';
import { formatPhoneNumber } from '@shared/helpers';
import { formatRatingValue } from '@shared/utils';

type CandidateListProps = {
  candidates: Candidate[];
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  recordsPerPageOptions?: number[];
  onRecordsPerPageChange?: (pageSize: number) => void;
  setPageSize: (pageSize: number) => void;
  isFilledFilters: boolean;
  onSortChange: (field: string, direction?: 'asc' | 'desc') => void;
  currentSort: Record<string, 'asc' | 'desc'>;
  isLoading: boolean;
  totalRecords: number;
};

export const CandidateList = ({
  candidates,
  page,
  setPage,
  pageSize,
  recordsPerPageOptions,
  onRecordsPerPageChange,
  setPageSize,
  isFilledFilters,
  onSortChange,
  currentSort,
  isLoading,
  totalRecords,
}: CandidateListProps) => {
  const navigate = useNavigate();
  const ratingsAndReviewsEnabled = useFeatureFlag('ratingsAndReviews');

  const emptyState = useMemo(() => {
    if (isFilledFilters) {
      return {
        desc: 'К сожалению, совпадений не найдено',
        subDesc:
          'Попробуйте изменить параметры фильтров. Чтобы вернуться ко всему списку, сбросьте фильтр.',
      };
    }

    return {
      desc: 'Список кандидатов пуст',
      subDesc: 'Кандидаты отобразятся после выбора на данную вакансию',
    };
  }, [isFilledFilters]);

  const columns = useMemo(
    () => [
      {
        accessor: 'id',
        title: (
          <CellSort
            label='ID'
            onSort={dir => onSortChange('id', dir)}
            currentDirection={currentSort.id}
          />
        ),
      },
      {
        accessor: 'createdAt',
        title: (
          <CellSort
            label='Дата создания'
            onSort={dir => onSortChange('createdAt', dir)}
            currentDirection={currentSort.createdAt}
          />
        ),
        render: (record: Candidate) => {
          if (!record?.createdAt) {
            return <Text>-</Text>;
          }

          return dayjs(record.createdAt).format('DD.MM.YYYY HH:mm');
        },
      },
      {
        accessor: 'phone',
        title: (
          <CellSort
            label='Контактный телефон'
            onSort={dir => onSortChange('phone', dir)}
            currentDirection={currentSort.phone}
          />
        ),
        render: (record: Candidate) => {
          if (!record?.phone) {
            return '-';
          }

          return formatPhoneNumber(record.phone) || '-';
        },
      },
      {
        accessor: 'fio',
        title: (
          <CellSort
            label='Фамилия Имя'
            onSort={dir => onSortChange('fullName', dir)}
            currentDirection={currentSort.fullName}
          />
        ),
        render: (record: Candidate) => getCandidateDisplayName(record),
      },
      ...(ratingsAndReviewsEnabled
        ? [
            {
              accessor: 'rating',
              title: 'Оценка',
              render: (record: Candidate) => {
                if (record.rating == null) {
                  return '-';
                }

                return formatRatingValue(record.rating);
              },
            },
          ]
        : []),
      {
        accessor: 'interviewRequired',
        title: (
          <CellSort
            label='Интервью'
            onSort={dir => onSortChange('interview_required', dir)}
            currentDirection={currentSort.interview_required}
          />
        ),
        render: (record: Candidate) =>
          record.interviewRequired ? (
            <Tooltip label='Требует участия' position='top' withArrow>
              <IconAlertTriangle size={16} color='var(--mantine-color-red-7)' />
            </Tooltip>
          ) : (
            <Tooltip label='Не требует участия' position='top' withArrow>
              <IconChecks size={16} color='var(--mantine-color-green-7)' />
            </Tooltip>
          ),
      },
      {
        accessor: 'candidateVacancyStatus',
        title: (
          <CellSort
            label='Персональные данные'
            onSort={dir => onSortChange('candidateVacancyStatus', dir)}
            currentDirection={currentSort.candidateVacancyStatus}
          />
        ),
        render: (record: Candidate) => {
          return record.candidateVacancyStatus;
        },
      },
      {
        accessor: 'status',
        title: (
          <CellSort
            label='Статус'
            onSort={dir => onSortChange('status', dir)}
            currentDirection={currentSort.status}
          />
        ),
        render: (record: Candidate) => (
          <CandidateStatusBadge status={record.status} />
        ),
      },
    ],
    [ratingsAndReviewsEnabled, currentSort, onSortChange]
  );

  const handleRowClick = ({ record }: { record: Candidate }) => {
    navigate(`/candidate-list/${record.id}`);
  };

  return (
    <DataTable<Candidate>
      fetching={isLoading}
      columns={columns}
      records={candidates}
      onRowClick={handleRowClick}
      page={page}
      setPage={setPage}
      recordsPerPageOptions={recordsPerPageOptions}
      onRecordsPerPageChange={onRecordsPerPageChange}
      pageSize={pageSize}
      setPageSize={setPageSize}
      recordsPerPage={pageSize}
      onPageChange={setPage}
      totalRecords={totalRecords}
      emptyState={
        <EmptyRowsFallback
          desc={emptyState.desc}
          subDesc={emptyState.subDesc}
        />
      }
      noRecordsText='Список вакансий пуст'
      highlightOnHover
    />
  );
};
